// DelightMoments.tsx - Memorable moments that make Cipher special
// Three specific delight moments: score reveal, streak system, market open

import { useState, useEffect, useRef } from 'react';
import { Flame, Sparkles, TrendingUp, TrendingDown, Bell } from 'lucide-react';
import { loadProfile } from '../../services/personalizationService';
import './DelightMoments.css';

// ============================================================================
// DELIGHT 1: Animated Cipher Score Reveal
// ============================================================================

interface AnimatedScoreProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  onAnimationComplete?: () => void;
}

export function AnimatedApexScore({
  score,
  size = 'md',
  showTooltip = false,
  onAnimationComplete
}: AnimatedScoreProps) {
  const [displayScore, setDisplayScore] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [showScoreTooltip, setShowScoreTooltip] = useState(false);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (hasAnimated.current) return;
    hasAnimated.current = true;

    const duration = 800;
    const startTime = Date.now();

    // Play subtle tick sound - optional, using Web Audio API
    if (typeof AudioContext !== 'undefined') {
      try {
        const audioCtx = new AudioContext();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(440, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.05);

        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + 0.05);
      } catch {
        // Audio not available - silent fail is fine
      }
    }

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * score));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        if (showTooltip) {
          setTimeout(() => setShowScoreTooltip(true), 200);
        }
        onAnimationComplete?.();
      }
    };

    requestAnimationFrame(animate);
  }, [score, showTooltip, onAnimationComplete]);

  const getScoreColor = () => {
    if (displayScore >= 80) return 'excellent';
    if (displayScore >= 60) return 'good';
    if (displayScore >= 40) return 'neutral';
    return 'poor';
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (displayScore / 100) * circumference;

  return (
    <div className={`animated-score ${size} ${isAnimating ? 'animating' : ''}`}>
      <svg viewBox="0 0 100 100" className="score-ring">
        <circle
          className="score-ring-bg"
          cx="50"
          cy="50"
          r="45"
          strokeWidth="6"
          fill="none"
        />
        <circle
          className={`score-ring-progress ${getScoreColor()}`}
          cx="50"
          cy="50"
          r="45"
          strokeWidth="6"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className={`score-value ${getScoreColor()}`}>
        {displayScore}
      </div>

      {showScoreTooltip && (
        <div className="score-tooltip">
          <Sparkles size={14} />
          <span>This is your Cipher Score — a proprietary 0-100 rating combining 5 intelligence signals.</span>
          <button onClick={() => setShowScoreTooltip(false)}>Got it</button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DELIGHT 2: Streak System
// ============================================================================

interface StreakDisplayProps {
  compact?: boolean;
}

export function StreakDisplay({ compact = false }: StreakDisplayProps) {
  const [streak, setStreak] = useState(0);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const profile = loadProfile();
    setStreak(profile.streak_days || 0);

    // Show celebration message for milestone streaks
    if (profile.streak_days === 3 || profile.streak_days === 7 || profile.streak_days === 30) {
      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 5000);
    }
  }, []);

  if (streak < 1) return null;

  const getMessage = () => {
    if (streak >= 30) return "30-day streak — you're a serious investor.";
    if (streak >= 7) return "7-day streak — you're building a consistent research habit.";
    if (streak >= 3) return "3-day streak — your Cipher intelligence is building.";
    return null;
  };

  if (compact) {
    return (
      <div className="streak-compact" title={`${streak}-day streak`}>
        <Flame size={14} className={streak >= 7 ? 'hot' : ''} />
        <span>{streak}</span>
      </div>
    );
  }

  return (
    <div className="streak-display">
      <div className="streak-badge">
        <Flame size={18} className={streak >= 7 ? 'hot' : streak >= 3 ? 'warm' : ''} />
        <span className="streak-count">{streak}</span>
        <span className="streak-label">day streak</span>
      </div>

      {showMessage && getMessage() && (
        <div className="streak-message">
          <span>{getMessage()}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DELIGHT 3: Market Open Celebration
// ============================================================================

interface MarketOpenCelebrationProps {
  spyChange?: number;
  topEvents?: string[];
}

export function MarketOpenCelebration({ spyChange = 0.45, topEvents = [] }: MarketOpenCelebrationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    const checkMarketOpen = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const day = etTime.getDay();
      const hours = etTime.getHours();
      const minutes = etTime.getMinutes();

      // Check if weekday and exactly 9:30 AM ET
      const isWeekday = day >= 1 && day <= 5;
      const isMarketOpen = hours === 9 && minutes >= 30 && minutes <= 31;

      // Check if already shown today
      const shownToday = sessionStorage.getItem('market_open_shown');
      const today = etTime.toDateString();

      if (isWeekday && isMarketOpen && shownToday !== today && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('market_open_shown', today);

        // Auto-dismiss after 6 seconds
        setTimeout(() => setIsVisible(false), 6000);
      }
    };

    // Check immediately and then every 30 seconds
    checkMarketOpen();
    const interval = setInterval(checkMarketOpen, 30000);

    return () => clearInterval(interval);
  }, [hasShown]);

  // For demo/testing: can force show
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail?.type === 'market-open') {
        setIsVisible(true);
        setTimeout(() => setIsVisible(false), 6000);
      }
    };
    window.addEventListener('demo-event' as any, handler);
    return () => window.removeEventListener('demo-event' as any, handler);
  }, []);

  if (!isVisible) return null;

  const isUp = spyChange >= 0;
  const defaultEvents = ['NVDA earnings after close', 'Fed minutes released at 2pm'];
  const events = topEvents.length > 0 ? topEvents : defaultEvents;

  return (
    <div className="market-open-celebration" onClick={() => setIsVisible(false)}>
      <div className="market-open-glow" />
      <div className="market-open-content">
        <div className="market-open-header">
          <div className="market-open-icon">
            <Bell size={20} />
          </div>
          <span className="market-open-title">Markets are open</span>
        </div>

        <div className="market-open-summary">
          <div className={`market-open-spy ${isUp ? 'up' : 'down'}`}>
            {isUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span>SPY indicated {isUp ? 'up' : 'down'} {Math.abs(spyChange).toFixed(2)}% in pre-market</span>
          </div>

          {events.length > 0 && (
            <div className="market-open-events">
              <span className="events-label">Today's key events:</span>
              <ul>
                {events.slice(0, 2).map((event, i) => (
                  <li key={i}>{event}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <span className="market-open-dismiss">Click to dismiss</span>
      </div>
    </div>
  );
}

// ============================================================================
// Watchlist Suggestion Banner
// ============================================================================

interface WatchlistSuggestionProps {
  ticker: string;
  viewCount: number;
  onAdd: () => void;
  onDismiss: () => void;
}

export function WatchlistSuggestion({ ticker, viewCount, onAdd, onDismiss }: WatchlistSuggestionProps) {
  return (
    <div className="watchlist-suggestion">
      <span className="suggestion-text">
        You've been researching <strong>{ticker}</strong> ({viewCount} views) — add it to your watchlist?
      </span>
      <div className="suggestion-actions">
        <button className="suggestion-add" onClick={onAdd}>Add to Watchlist</button>
        <button className="suggestion-dismiss" onClick={onDismiss}>Not now</button>
      </div>
    </div>
  );
}

export default {
  AnimatedApexScore,
  StreakDisplay,
  MarketOpenCelebration,
  WatchlistSuggestion,
};
